import { FEATURES, MODAL, SEARCH_GROUPS } from '@constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'
import store from '@state/store'

/**
 * Defense-in-depth route guard for the gestor-only settings sub-routes
 * (`templates`, `automacoes`). `tags` e `campos` NÃO são mais gestor-only
 * (I6 — ownership): qualquer profissional cria/gerencia as próprias tags e
 * campos personalizados; o backend é o gate final (`require_gestor_or_owner`).
 * Mirrors the `src/utils/AdditionalPermissions.js` pattern so we never reach
 * for the implicit `window.app` global at boot time. The Vuex store is always
 * initialized before the router, making this safe at `beforeEnter` time.
 */
function requireGestor(to, from, next) {
  let user = store.getters['user/currentUser'] || {}
  if (!user.IS_GESTOR) {
    return next({ name: 'task.board' })
  }
  return next()
}

export default [
  {
    path: '/task',
    name: 'task',
    redirect: '/task/overview',
    meta: {
      can_show: FEATURES.TASK_V2,
      search: {
        title: 'Gerenciador de Tarefas',
        description: 'Gestão de tarefas, sprints e quadros do escritório',
        keywords: ['tarefas', 'gerenciador', 'projetos', 'sprints', 'kanban'],
        icon: 'mdi-clipboard-check-outline',
        group: SEARCH_GROUPS.FERRAMENTAS,
      },
    },
    component: () =>
      lazyLoadView(
        import('@modules/tarefas/views/nav-bar/tarefas-nav-bar.vue')
      ),
    children: [
      {
        path: 'overview',
        name: 'task.overview',
        meta: {
          can_show: FEATURES.TASK_V2,
          search: {
            title: 'Tarefas — Visão Geral',
            description: 'Painel com indicadores e resumo das tarefas',
            keywords: [
              'visão geral',
              'painel',
              'indicadores',
              'tarefas',
              'resumo',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/tarefas/views/overview/view-task-overview.vue')
          ),
      },
      {
        path: 'board',
        name: 'task.board',
        redirect: { name: 'task.board.list' },
        meta: {
          can_show: FEATURES.TASK_V2,
          search: {
            title: 'Quadro de Tarefas',
            description: 'Quadro de tarefas em lista e kanban',
            keywords: ['quadro', 'board', 'kanban', 'lista', 'tarefas'],
            group: SEARCH_GROUPS.FERRAMENTAS,
            actions: [
              {
                id: 'criar-tarefa',
                title: 'Criar tarefa',
                keywords: ['nova tarefa', 'adicionar tarefa', 'task'],
                to: { name: 'task.board' },
                modal: MODAL.TASK_DETAIL,
              },
              {
                id: 'criar-pasta-tarefas',
                title: 'Criar pasta de tarefas',
                keywords: ['nova pasta', 'pasta', 'folder'],
                to: { name: 'task.board' },
                modal: MODAL.TASK_FOLDER_SAVE,
              },
            ],
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/tarefas/views/board/view-task-board.vue')
          ),
        children: [
          {
            // Optional ClickUp short-link suffix (`l/<f>/s/<s>/t/<t>` or
            // `t/<t>`). A single optional regex param captures the whole
            // suffix so a fresh reload/deep-link to
            // `/task/board/list/l/<f>/s/<s>/t/<t>` mounts the board (same
            // route name). The suffix is parsed by a pure helper in the view
            // (`parseShortlinkContextFromPathname`), mirroring the manual-URL
            // style — vue-router only matches; it never owns the parse.
            path: 'list/:shortlink(l/.*|t/.*)?',
            name: 'task.board.list',
            meta: {
              can_show: FEATURES.TASK_V2,
              search: {
                title: 'Tarefas — Lista',
                description: 'Visualização em lista das tarefas do quadro',
                keywords: ['lista', 'tarefas', 'quadro', 'tabela'],
                icon: 'mdi-format-list-bulleted',
                group: SEARCH_GROUPS.FERRAMENTAS,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/components/task-list-view/task-list-view.vue'
                )
              ),
          },
          {
            path: 'kanban/:shortlink(l/.*|t/.*)?',
            name: 'task.board.kanban',
            meta: {
              can_show: FEATURES.TASK_V2,
              search: {
                title: 'Tarefas — Kanban',
                description: 'Quadro Kanban das tarefas por status',
                keywords: ['kanban', 'tarefas', 'quadro', 'board'],
                icon: 'mdi-view-column-outline',
                group: SEARCH_GROUPS.FERRAMENTAS,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/components/task-kanban-view/task-kanban-view.vue'
                )
              ),
          },
          {
            path: 'gantt/:shortlink(l/.*|t/.*)?',
            name: 'task.board.gantt',
            // Sem meta.search de propósito: o Gantt ainda não foi implementado,
            // então não deve ser oferecido como opção na busca global (Ctrl+K).
            meta: {
              can_show: FEATURES.TASK_V2,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/components/task-gantt-view/task-gantt-view.vue'
                )
              ),
          },
          {
            // Mesmo padrão de sufixo opcional de shortlink das irmãs acima —
            // ver comentário em `list` (linha ~90).
            path: 'agenda/:shortlink(l/.*|t/.*)?',
            name: 'task.board.agenda',
            meta: {
              can_show: FEATURES.TASK_V2,
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/components/task-agenda-view/task-agenda-view.vue'
                )
              ),
          },
        ],
      },
      {
        path: 'configuracoes',
        name: 'task.configuracoes',
        redirect: { name: 'task.configuracoes.tags' },
        meta: {
          can_show: FEATURES.TASK_V2,
          // Configurações não é mais gestor_only (I6 — ownership); redireciona
          // para Tags, aberto a todos. Templates/Automações seguem gestor-only
          // via requireGestor nas próprias sub-rotas.
          search: {
            title: 'Configurações de Tarefas',
            description: 'Tags, campos, templates e automações de tarefas',
            keywords: [
              'configurações',
              'tags',
              'campos',
              'templates',
              'automações',
            ],
            group: SEARCH_GROUPS.FERRAMENTAS,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/tarefas/views/settings/view-task-settings.vue')
          ),
        children: [
          {
            // Aberto a qualquer profissional (I6 — ownership em tags).
            path: 'tags',
            name: 'task.configuracoes.tags',
            meta: {
              can_show: FEATURES.TASK_V2,
              search: {
                title: 'Tarefas — Tags',
                description: 'Tags do escritório para classificar tarefas',
                keywords: ['tags', 'etiquetas', 'tarefas', 'classificar'],
                icon: 'mdi-tag-multiple-outline',
                group: SEARCH_GROUPS.FERRAMENTAS,
                actions: [
                  {
                    id: 'criar-tag',
                    title: 'Criar tag',
                    keywords: ['nova tag', 'etiqueta'],
                    to: { name: 'task.configuracoes.tags' },
                    modal: MODAL.TAG_FORM,
                  },
                ],
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/views/settings/view-task-settings-tags.vue'
                )
              ),
          },
          {
            // Aberto a qualquer profissional (I6 — ownership em campos).
            path: 'campos',
            name: 'task.configuracoes.campos',
            meta: {
              can_show: FEATURES.TASK_V2,
              search: {
                title: 'Tarefas — Campos',
                description: 'Campos personalizados para tarefas',
                keywords: [
                  'campos',
                  'campos personalizados',
                  'custom fields',
                  'tarefas',
                ],
                icon: 'mdi-form-select',
                group: SEARCH_GROUPS.FERRAMENTAS,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/views/settings/view-task-settings-fields.vue'
                )
              ),
          },
          {
            path: 'templates',
            name: 'task.configuracoes.templates',
            meta: {
              can_show: FEATURES.TASK_V2,
              gestor_only: true,
              search: {
                title: 'Tarefas — Templates',
                description: 'Templates reutilizáveis de árvore de tarefas',
                keywords: ['templates', 'modelo de tarefa', 'tarefas'],
                icon: 'mdi-file-document-multiple-outline',
                group: SEARCH_GROUPS.FERRAMENTAS,
                actions: [
                  {
                    id: 'criar-template-tarefa',
                    title: 'Criar template de tarefa',
                    keywords: ['novo template', 'modelo de tarefa'],
                    to: { name: 'task.configuracoes.templates' },
                    modal: MODAL.TAREFAS_TEMPLATE_FORM,
                  },
                ],
              },
            },
            beforeEnter: requireGestor,
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/views/settings/view-task-settings-templates.vue'
                )
              ),
          },
          {
            path: 'modelos',
            name: 'task.configuracoes.modelos',
            meta: {
              can_show: FEATURES.TASK_V2,
              gestor_only: true,
              search: {
                title: 'Tarefas — Modelos de Lista',
                description: 'Modelos reutilizáveis de lista de tarefas',
                keywords: [
                  'modelos de lista',
                  'modelo de lista',
                  'tarefas',
                  'sprint',
                ],
                icon: 'mdi-clipboard-text-multiple-outline',
                group: SEARCH_GROUPS.FERRAMENTAS,
              },
            },
            beforeEnter: requireGestor,
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/views/settings/view-task-settings-list-templates.vue'
                )
              ),
          },
          {
            path: 'automacoes',
            name: 'task.configuracoes.automatizacoes',
            meta: {
              can_show: FEATURES.TASK_V2,
              gestor_only: true,
              search: {
                title: 'Tarefas — Automações',
                description: 'Automações de tarefas por gatilho',
                keywords: ['automações', 'gatilhos', 'tarefas'],
                icon: 'mdi-lightning-bolt-outline',
                group: SEARCH_GROUPS.FERRAMENTAS,
              },
            },
            beforeEnter: requireGestor,
            component: () =>
              lazyLoadView(
                import(
                  '@modules/tarefas/views/settings/view-task-settings-triggers.vue'
                )
              ),
          },
        ],
      },
    ],
  },
]
